/* ==========================================================================
   DEMO ONLY: Display Preferences Panel & Shared Hook
   ========================================================================== */
import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { prefersReducedMotion } from './countUp'
import styles from './DemoOptionsPanel.module.css'
import { GUIDE_SEEN_KEY, hasSeenGuide } from './BriefGuide'
import { HubBannerStyle } from './IntelligenceHubScene'
import { FleetValueFormulaStyle } from './FleetValueFormula'

export interface DemoOptions {
  showOverviewKeyInsights: boolean
  showAllOverviewBriefs: boolean
  briefTicketStub: boolean
  fleetValueFormula: FleetValueFormulaStyle
  showHubMetrics: boolean
  showHubPitch: boolean
  hubBannerStyle: HubBannerStyle
  tourStyle: TourStyle
  assistantName: AssistantName
  briefsView: BriefsView
  showComingSoonBriefs: boolean
}

/* how the Briefs tab lays out its briefs: the lifecycle board, a list or shelves */
export type BriefsView = 'board' | 'list' | 'shelf'

const BRIEFS_VIEWS: { value: BriefsView; label: string }[] = [
  { value: 'board', label: 'Lifecycle' },
  { value: 'list', label: 'List' },
  { value: 'shelf', label: 'Library' },
]

/* what the brief's AI assistant is called: its panel heading, and the matching tour step */
export type AssistantName = 'mission-control' | 'help' | 'partner' | 'assistant' | 'anything'

export const ASSISTANT_NAMES: { value: AssistantName; label: string; tourTitle: string }[] = [
  { value: 'mission-control', label: 'Mission Control', tourTitle: 'Ask Mission Control' },
  { value: 'help', label: 'How can I help?', tourTitle: 'Ask a question' },
  { value: 'partner', label: 'Ask Partner Intelligence', tourTitle: 'Ask Partner Intelligence' },
  { value: 'assistant', label: 'Brief assistant', tourTitle: 'Ask the brief assistant' },
  { value: 'anything', label: 'Ask me anything', tourTitle: 'Ask me anything' },
]

export const assistantNameFor = (value: AssistantName) =>
  ASSISTANT_NAMES.find(name => name.value === value) ?? ASSISTANT_NAMES[0]

export type TourStyle = 'highlights' | 'diagram'

const TOUR_STYLES: { value: TourStyle; label: string }[] = [
  { value: 'highlights', label: 'Highlights' },
  { value: 'diagram', label: 'Diagram' },
]

const FORMULA_STYLES: { value: FleetValueFormulaStyle; label: string }[] = [
  { value: 'formula', label: 'Formula' },
  { value: 'text', label: 'Text' },
]

const BANNER_STYLES: { value: HubBannerStyle; label: string }[] = [
  { value: 'orbit', label: 'Orbit' },
  { value: 'pipeline', label: 'Circuit' },
  { value: 'horizon', label: 'Horizon' },
  { value: 'colony', label: 'Colony' },
]

const DEFAULT_OPTIONS: DemoOptions = {
  showOverviewKeyInsights: false,
  showAllOverviewBriefs: false,
  briefTicketStub: true,
  fleetValueFormula: 'formula',
  showHubMetrics: true,
  showHubPitch: true,
  hubBannerStyle: 'pipeline', // Circuit
  tourStyle: 'diagram',
  assistantName: 'anything',
  briefsView: 'board',
  showComingSoonBriefs: true,
}

/* saved options over the defaults; a fleet value formula style that's since been retired reads as the formula */
const withDefaults = (saved: Partial<DemoOptions>): DemoOptions => {
  const options = { ...DEFAULT_OPTIONS, ...saved }
  return options.fleetValueFormula === 'text' ? options : { ...options, fleetValueFormula: 'formula' }
}

const STORAGE_KEY = 'intelligence_hub_demo_options'
const UPDATE_EVENT = 'intelligence_hub_demo_options_changed'

export function useDemoOptions() {
  const [options, setOptions] = useState<DemoOptions>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return saved ? withDefaults(JSON.parse(saved)) : DEFAULT_OPTIONS
    } catch {
      return DEFAULT_OPTIONS
    }
  })

  useEffect(() => {
    const handleUpdate = () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY)
        setOptions(saved ? withDefaults(JSON.parse(saved)) : DEFAULT_OPTIONS)
      } catch {}
    }
    window.addEventListener(UPDATE_EVENT, handleUpdate)
    return () => window.removeEventListener(UPDATE_EVENT, handleUpdate)
  }, [])

  const updateOption = <K extends keyof DemoOptions>(key: K, value: DemoOptions[K]) => {
    const updated = { ...options, [key]: value }
    setOptions(updated)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
      window.dispatchEvent(new Event(UPDATE_EVENT))
    } catch {}
  }

  // back to the defaults: the saved choices are dropped, so later changes to the defaults apply too
  const resetOptions = () => {
    setOptions(DEFAULT_OPTIONS)
    try {
      localStorage.removeItem(STORAGE_KEY)
      window.dispatchEvent(new Event(UPDATE_EVENT))
    } catch {}
  }

  const isDefault = (Object.keys(DEFAULT_OPTIONS) as (keyof DemoOptions)[]).every(
    key => options[key] === DEFAULT_OPTIONS[key]
  )

  return { options, updateOption, resetOptions, isDefault }
}

/* a segmented toggle's selected pill slides to the chosen option: the group notes how many options it has and which
   is chosen, for the pill's width and place */
const pillAt = <T,>(choices: { value: T }[], chosen: T) => (el: HTMLDivElement | null) => {
  el?.style.setProperty('--count', String(choices.length))
  el?.style.setProperty(
    '--index',
    String(
      Math.max(
        0,
        choices.findIndex(choice => choice.value === chosen)
      )
    )
  )
}

/* which part of the Hub is on screen, from the address: the Hub's landing page, a customer's Overview, Intel or
   Briefs tab, or a brief, so the panel can mark the group of settings for it */
type Area = 'hub' | 'overview' | 'intel' | 'briefs' | 'brief'

const areaAt = (path: string): Area | undefined => {
  const [, hub, customer, tab, brief] = path.split('/').filter((part, i) => i === 0 || part)
  if (hub !== 'intelligence-hub') return undefined
  if (!customer) return 'hub'
  if (tab === 'briefs') return brief ? 'brief' : 'briefs'
  if (tab === 'intel') return 'intel'
  return tab ? undefined : 'overview' // Admin tools has no settings here
}

export const DemoOptionsPanel: FunctionComponent = () => {
  const [isExpanded, setIsExpanded] = useState(false)
  // the area on screen as the panel opens (it's only ever open over one page)
  const area = isExpanded ? areaAt(window.location.pathname) : undefined
  const groupFor = (group: Area) => ({
    className: styles.groupSection,
    'data-current': area === group || undefined,
    'aria-current': area === group ? ('page' as const) : undefined,
  })
  // the panel morphs back into the button before it goes (as a liquid-glass menu does)
  const [isClosing, setIsClosing] = useState(false)
  const close = () => {
    if (prefersReducedMotion()) {
      setIsExpanded(false)
      return
    }
    setIsClosing(true)
  }
  useEffect(() => {
    if (!isClosing) return undefined
    const done = window.setTimeout(() => {
      setIsClosing(false)
      setIsExpanded(false)
    }, 260)
    return () => window.clearTimeout(done)
  }, [isClosing])
  const { options, updateOption, resetOptions, isDefault } = useDemoOptions()
  const [guideSeen, setGuideSeen] = useState(hasSeenGuide)

  // the panel closes on a click anywhere outside it, as a popover does
  const wrapperRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!isExpanded) return undefined
    const outside = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) close()
    }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- close is the same each time
  }, [isExpanded])

  const resetGuide = () => {
    try {
      localStorage.removeItem(GUIDE_SEEN_KEY)
    } catch {}
    setGuideSeen(false)
  }

  return (
    <div className={`${styles.demoWidgetWrapper}${isExpanded ? ` ${styles.expanded}` : ''}`} ref={wrapperRef}>
      {!isExpanded ? (
        <button
          type='button'
          className={styles.triggerButton}
          onClick={() => setIsExpanded(true)}
          aria-label='Open display preferences'
          title='Display preferences'
        >
          <svg viewBox='0 0 24 24' width='20' height='20' fill='none' stroke='currentColor' strokeWidth='2'>
            <line x1='4' y1='21' x2='4' y2='14' />
            <line x1='4' y1='10' x2='4' y2='3' />
            <line x1='12' y1='21' x2='12' y2='12' />
            <line x1='12' y1='8' x2='12' y2='3' />
            <line x1='20' y1='21' x2='20' y2='16' />
            <line x1='20' y1='12' x2='20' y2='3' />
            <line x1='1' y1='14' x2='7' y2='14' />
            <line x1='9' y1='8' x2='15' y2='8' />
            <line x1='17' y1='16' x2='23' y2='16' />
          </svg>
        </button>
      ) : (
        <div className={styles.overlayPanel} data-closing={isClosing || undefined}>
          <header className={styles.panelHeader}>
            <h4>Display preferences</h4>
            <div className={styles.headerActions}>
              <button
                type='button'
                className={`${styles.closeButton} ${styles.resetButton}`}
                onClick={resetOptions}
                disabled={isDefault}
                aria-label='Reset to defaults'
                title='Reset to defaults'
              >
                <svg
                  viewBox='0 0 24 24'
                  width='16'
                  height='16'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M3 12a9 9 0 1 0 3-6.7' />
                  <path d='M3 3v6h6' />
                </svg>
              </button>
              <button type='button' className={styles.closeButton} onClick={close} aria-label='Close options panel'>
                <svg viewBox='0 0 24 24' width='18' height='18' fill='none' stroke='currentColor' strokeWidth='2'>
                  <line x1='18' y1='6' x2='6' y2='18' />
                  <line x1='6' y1='6' x2='18' y2='18' />
                </svg>
              </button>
            </div>
          </header>

          <div className={styles.panelBody}>
            {/* Group 1: Intelligence Hub Main Landing Page */}
            <div {...groupFor('hub')}>
              <h5 className={styles.groupHeader}>Intelligence Hub</h5>

              <label className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Pitch banner</strong>
                  <small>The intro banner atop the Hub</small>
                </div>
                <span className={styles.switch}>
                  <input
                    type='checkbox'
                    checked={options.showHubPitch}
                    onChange={e => updateOption('showHubPitch', e.target.checked)}
                    className={styles.switchInput}
                  />
                  <span className={styles.switchSlider} aria-hidden='true' />
                </span>
              </label>

              {/* which pitch banner: a segmented toggle under its switch */}
              <div
                className={styles.segmented}
                role='group'
                aria-label='Pitch banner style'
                ref={pillAt(BANNER_STYLES, options.hubBannerStyle)}
              >
                {BANNER_STYLES.map(style => (
                  <button
                    key={style.value}
                    type='button'
                    className={options.hubBannerStyle === style.value ? styles.segmentActive : styles.segment}
                    aria-pressed={options.hubBannerStyle === style.value}
                    disabled={!options.showHubPitch}
                    onClick={() => updateOption('hubBannerStyle', style.value)}
                  >
                    {style.label}
                  </button>
                ))}
              </div>

              <label className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Metric cards</strong>
                  <small>Devices, volume, risk flags and fleet value</small>
                </div>
                <span className={styles.switch}>
                  <input
                    type='checkbox'
                    checked={options.showHubMetrics}
                    onChange={e => updateOption('showHubMetrics', e.target.checked)}
                    className={styles.switchInput}
                  />
                  <span className={styles.switchSlider} aria-hidden='true' />
                </span>
              </label>
            </div>

            {/* Group 2: a customer's Overview tab */}
            <div {...groupFor('overview')}>
              <h5 className={styles.groupHeader}>Overview tab</h5>

              <label className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Key insights</strong>
                  <small>Insights and suggestions beside the highlights</small>
                </div>
                <span className={styles.switch}>
                  <input
                    type='checkbox'
                    checked={options.showOverviewKeyInsights}
                    onChange={e => updateOption('showOverviewKeyInsights', e.target.checked)}
                    className={styles.switchInput}
                  />
                  <span className={styles.switchSlider} aria-hidden='true' />
                </span>
              </label>

              <label className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Brief pager</strong>
                  <small>Page through up to 3 latest briefs, not just the Health check</small>
                </div>
                <span className={styles.switch}>
                  <input
                    type='checkbox'
                    checked={options.showAllOverviewBriefs}
                    onChange={e => updateOption('showAllOverviewBriefs', e.target.checked)}
                    className={styles.switchInput}
                  />
                  <span className={styles.switchSlider} aria-hidden='true' />
                </span>
              </label>
            </div>

            {/* Group 3: a customer's Intel tab */}
            <div {...groupFor('intel')}>
              <h5 className={styles.groupHeader}>Intel tab</h5>

              <div className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Fleet value formula</strong>
                  <small>How the estimated fleet value is explained</small>
                </div>
              </div>
              <div
                className={styles.segmented}
                role='group'
                aria-label='Fleet value formula style'
                ref={pillAt(FORMULA_STYLES, options.fleetValueFormula)}
              >
                {FORMULA_STYLES.map(style => (
                  <button
                    key={style.value}
                    type='button'
                    className={options.fleetValueFormula === style.value ? styles.segmentActive : styles.segment}
                    aria-pressed={options.fleetValueFormula === style.value}
                    onClick={() => updateOption('fleetValueFormula', style.value)}
                  >
                    {style.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Group 4: Briefs tab */}
            <div {...groupFor('briefs')}>
              <h5 className={styles.groupHeader}>Briefs tab</h5>

              <div className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Layout</strong>
                  <small>Lifecycle wallets, a list or library shelves</small>
                </div>
              </div>
              <div
                className={styles.segmented}
                role='group'
                aria-label='Briefs layout'
                ref={pillAt(BRIEFS_VIEWS, options.briefsView)}
              >
                {BRIEFS_VIEWS.map(view => (
                  <button
                    key={view.value}
                    type='button'
                    className={options.briefsView === view.value ? styles.segmentActive : styles.segment}
                    aria-pressed={options.briefsView === view.value}
                    onClick={() => updateOption('briefsView', view.value)}
                  >
                    {view.label}
                  </button>
                ))}
              </div>

              <label className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Coming soon briefs</strong>
                  <small>Off, folded into one "+N coming soon"</small>
                </div>
                <span className={styles.switch}>
                  <input
                    type='checkbox'
                    checked={options.showComingSoonBriefs}
                    onChange={e => updateOption('showComingSoonBriefs', e.target.checked)}
                    className={styles.switchInput}
                  />
                  <span className={styles.switchSlider} aria-hidden='true' />
                </span>
              </label>
            </div>

            {/* Group 5: Brief */}
            <div {...groupFor('brief')}>
              <h5 className={styles.groupHeader}>Brief</h5>

              <label className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Ticket stub headline</strong>
                  <small>Headline numbers on a perforated stub</small>
                </div>
                <span className={styles.switch}>
                  <input
                    type='checkbox'
                    checked={options.briefTicketStub}
                    onChange={e => updateOption('briefTicketStub', e.target.checked)}
                    className={styles.switchInput}
                  />
                  <span className={styles.switchSlider} aria-hidden='true' />
                </span>
              </label>

              <div className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Tour</strong>
                  <small>{guideSeen ? 'Reset to show it again' : 'Shows on the next brief'}</small>
                </div>
                <button type='button' className='btn secondary sml' onClick={resetGuide} disabled={!guideSeen}>
                  Reset
                </button>
              </div>

              {/* how the tour shows: spotlighting each area in turn, or one diagram above the brief */}
              <div
                className={styles.segmented}
                role='group'
                aria-label='Tour style'
                ref={pillAt(TOUR_STYLES, options.tourStyle)}
              >
                {TOUR_STYLES.map(style => (
                  <button
                    key={style.value}
                    type='button'
                    className={options.tourStyle === style.value ? styles.segmentActive : styles.segment}
                    aria-pressed={options.tourStyle === style.value}
                    onClick={() => updateOption('tourStyle', style.value)}
                  >
                    {style.label}
                  </button>
                ))}
              </div>

              <div className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong id='demo-assistant-name'>AI assistant</strong>
                  <small>The name of the brief's assistant</small>
                </div>
              </div>
              <div className={`select sml ${styles.selectWrap}`}>
                <select
                  aria-labelledby='demo-assistant-name'
                  value={options.assistantName}
                  onChange={e => updateOption('assistantName', e.target.value as AssistantName)}
                >
                  {ASSISTANT_NAMES.map(name => (
                    <option key={name.value} value={name.value}>
                      {name.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
